/**
 * `<PrepActiveStageHeader>` — header strip rendered above the active-stage
 * prep on the Interview Prep tab.
 *
 * Phase 2.6 — interview-prep-spec.md §3.1. Left cluster shows the
 * "PREPARING FOR" eyebrow + stage name + status pill + provenance line.
 * Right cluster is owned by the parent (Generate / Regenerate / Cancel
 * are state-driven and live in `<InterviewPrepTab>`'s top bar).
 *
 * Props are intentionally minimal — the provenance line is a separate
 * component (`<PrepProvenanceLine>`) so empty-chain vs. enriched chain
 * copy can vary without re-rendering the header chrome.
 */
import type { JSX, ReactNode } from "react";
import { StatusPill } from "@/components/candidates/StatusPill";

interface PrepActiveStageHeaderProps {
  stageName: string;
  /** Pipeline-stage slug (e.g. "phone_screen") for the StatusPill. */
  stageSlug: string;
  /**
   * The JD / role name to display as the h2 title ("preparing for this role").
   * When provided, this renders as the large heading and stageName is used only
   * for the status pill. When absent, stageName fills the h2 as before.
   */
  roleName?: string | null;
  /** Provenance content — either `<PrepProvenanceLine>` or null. */
  provenance?: ReactNode;
  /** Optional right cluster — Generate / Regenerate / Cancel buttons. */
  actions?: ReactNode;
}

export function PrepActiveStageHeader({
  stageName,
  stageSlug,
  roleName,
  provenance,
  actions,
}: PrepActiveStageHeaderProps): JSX.Element {
  // Display the JD/role name as the primary heading when available.
  // The stage name is contextual (shown via StatusPill only) — it's a
  // pipeline-stage label ("Active", "Phone screen"), not a role title.
  const titleText = roleName ?? stageName;
  return (
    <header
      className="mb-6 flex items-center justify-between gap-4"
      data-testid="prep-active-stage-header"
    >
      <div className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-wider text-text-tertiary">
          Preparing for
        </span>
        <div className="flex items-center gap-2">
          <h2
            className="text-2xl font-semibold text-text-emphasis"
            data-testid="prep-active-stage-name"
          >
            {titleText}
          </h2>
          <StatusPill variant="pipeline" value={stageSlug} size="sm" />
        </div>
        {provenance ? <div className="mt-1">{provenance}</div> : null}
      </div>
      {actions ? (
        <div className="flex items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}
